import type { WasteMovementFormValues } from "../model/formValues";
import type { IWasteMovementEwcDefaultRule } from "@/customTypings/wasteMovement/WasteMovementDefaults";
import { defaultFieldKeyToFormKey } from "./wasteMovementDefaultFieldMapping";

export type EwcRuleFieldState = {
    ruleOwnedFields: Set<keyof WasteMovementFormValues>;
    userLockedFields: Set<keyof WasteMovementFormValues>;
};

export function createEwcRuleFieldState(): EwcRuleFieldState {
    return {
        ruleOwnedFields: new Set(),
        userLockedFields: new Set(),
    };
}

export function markFieldUserLocked(state: EwcRuleFieldState, fieldKey: string): void {
    const formKey = defaultFieldKeyToFormKey(fieldKey) ?? (fieldKey as keyof WasteMovementFormValues);
    state.userLockedFields.add(formKey);
    state.ruleOwnedFields.delete(formKey);
}

function findMatchingRule(ewcId: string, rules: IWasteMovementEwcDefaultRule[]): IWasteMovementEwcDefaultRule | null {
    if (!ewcId) return null;
    return (
        rules.find(
            (r) => !r.isDeleted && r.isEnabled && r.triggerEwcIds.some((id) => id === ewcId)
        ) ?? null
    );
}

export type ApplyEwcRuleResult = {
    updates: Partial<WasteMovementFormValues>;
    nextState: EwcRuleFieldState;
};

/**
 * When EWC changes: apply matching rule targets to rule-owned fields.
 * When no rule matches: clear rule-owned fields (not user-locked).
 */
export function applyEwcDefaultRule(
    ewcId: string,
    rules: IWasteMovementEwcDefaultRule[],
    state: EwcRuleFieldState
): ApplyEwcRuleResult {
    const nextState: EwcRuleFieldState = {
        ruleOwnedFields: new Set(state.ruleOwnedFields),
        userLockedFields: new Set(state.userLockedFields),
    };
    const updates: Partial<WasteMovementFormValues> = {};
    const rule = findMatchingRule(ewcId, rules);

    for (const fieldKey of nextState.ruleOwnedFields) {
        if (nextState.userLockedFields.has(fieldKey)) continue;
        updates[fieldKey] = "" as never;
        nextState.ruleOwnedFields.delete(fieldKey);
    }

    if (!rule) {
        return { updates, nextState };
    }

    for (const [defaultFieldKey, valueGuid] of Object.entries(rule.targetValues)) {
        const formKey = defaultFieldKeyToFormKey(defaultFieldKey);
        if (!formKey) continue;
        if (nextState.userLockedFields.has(formKey)) continue;
        updates[formKey] = valueGuid as never;
        nextState.ruleOwnedFields.add(formKey);
    }

    return { updates, nextState };
}

export function resolvedPreviewLabel(status: string, resolvedDisplay?: string | null): string {
    if (status === "Applied" && resolvedDisplay) return resolvedDisplay;
    if (status === "NoValueYet") return "No value yet";
    if (status === "NotAppliedInvalid") return "Not applied — selected value is no longer available";
    return "—";
}

export function modeDisplayLabel(mode: string): string {
    switch (mode) {
        case "SpecificValue":
            return "Specific Value";
        case "MostFrequent":
            return "Most Frequent";
        default:
            return "No Default";
    }
}
